import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { IMG_PATH } from "utils/constants/imgPath";
import { RenderCommaMoney } from "utils/functions/formatMoney";
import { SEAT_STATE_STYLE, ZONE_BADGE_COLOR } from "utils/functions/seatBooking";
import { Buttons, Displays, Icons, Inputs, Modals } from "components";

const ICON_PATH = "/assets/images/icons/seat-booking";
const LEGEND = ["free", "pending", "taken"];
// key คงที่ต่อรายการ ลบรายการกลางแล้ว dropdown/ช่องชื่อของรายการอื่นไม่สลับกัน
let entryUid = 0;
const newEntry = (patch = {}) => ({ uid: ++entryUid, seat_id: null, zone: null, row: "", viewer_name: "", ...patch });

// S2 (design ใหม่): รูปผังฮอลล์ (แตะโซนบนรูปได้ถ้าแอดมินวาดกรอบไว้) + ชิปโซน + ผังสถานะทั้งโซน
// + รายการที่นั่งแบบ dropdown แถว/เลข พร้อมชื่อผู้ชมต่อที่นั่ง แตะบนผังหรือเลือกจาก dropdown ได้ทั้งคู่
const SeatSelectStep = ({ info, map, draft, viewerNames, onBack, onNext }) => {
  const { t } = useTranslation(["seat"]);
  const seatById = useMemo(() => new Map(map.seats.map((s) => [s.seat_id, s])), [map.seats]);
  const zoneNameOf = useMemo(
    () => Object.fromEntries((info.zone_names || []).map((z) => [z.zone, z.name])),
    [info.zone_names]
  );
  // เรียงชิปโซนตามลำดับที่แอดมินตั้งชื่อไว้ (เช่น B1 A B2 ตามผังจริง) โซนที่ไม่ได้ตั้งชื่อต่อท้าย
  const zones = useMemo(() => {
    const order = (info.zone_names || []).map((z) => z.zone);
    const rank = (zone) => (order.includes(zone) ? order.indexOf(zone) : order.length);
    return [...map.zones].sort((a, b) => rank(a.zone) - rank(b.zone));
  }, [map.zones, info.zone_names]);

  // หนึ่งรายการ = หนึ่งที่นั่ง (seat_id ว่าง = ยังไม่ได้เลือก) zone/row เก็บไว้ให้ dropdown เลือกต่อได้
  const [_entries, _setEntries] = useState(() => {
    const fromDraft = (draft ? draft.seats : [])
      .map((s) => seatById.get(s.seat_id))
      .filter(Boolean)
      .map((seat) =>
        newEntry({
          seat_id: seat.seat_id,
          zone: seat.zone,
          row: seat.row,
          viewer_name: (viewerNames && viewerNames[seat.seat_id]) || "",
        })
      );
    return fromDraft.length ? fromDraft : [newEntry()];
  });
  const [_zone, _setZone] = useState(() => {
    const first = _entries[0] && _entries[0].zone;
    return first || (zones[0] && zones[0].zone);
  });
  const [_isZoomOpen, _setIsZoomOpen] = useState(false);

  const selectedIds = _entries.map((e) => e.seat_id).filter(Boolean);

  // ที่นั่ง "ของฉัน" (ล็อกไว้ใน draft) ที่เอาออกจากรายการแล้ว แสดงเป็นว่าง
  const stateOf = (seat) =>
    selectedIds.includes(seat.seat_id) ? "mine" : seat.state === "mine" ? "free" : seat.state;

  const seatsOfZone = map.seats.filter((s) => s.zone === _zone).map((s) => ({ ...s, state: stateOf(s) }));
  const zoneInfo = zones.find((z) => z.zone === _zone) || { free: 0, total: 0 };
  const zoneIndex = zones.findIndex((z) => z.zone === _zone);
  const zoneLabel = (zone) => (zoneNameOf[zone] ? `${zone} ${zoneNameOf[zone]}` : zone);

  const _updateEntry = (index, patch) =>
    _setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));

  // ลบรายการ (เหลือรายการเดียวจะล้างเป็นรายการว่างแทน)
  const _removeEntry = (index) =>
    _setEntries((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [newEntry()];
    });

  const _toggleSeat = (seat) => {
    const index = _entries.findIndex((e) => e.seat_id === seat.seat_id);
    if (index >= 0) {
      _removeEntry(index);
      return;
    }
    const picked = { seat_id: seat.seat_id, zone: seat.zone, row: seat.row };
    const emptyIndex = _entries.findIndex((e) => !e.seat_id);
    if (emptyIndex >= 0) {
      _updateEntry(emptyIndex, picked);
      return;
    }
    if (_entries.length >= info.max_per_booking) {
      toast.info(t("You can book up to {{count}} seats at a time", { ns: "seat", count: info.max_per_booking }));
      return;
    }
    _setEntries((prev) => [...prev, newEntry(picked)]);
  };

  const _addEntry = () => {
    if (_entries.length >= info.max_per_booking) {
      toast.info(t("You can book up to {{count}} seats at a time", { ns: "seat", count: info.max_per_booking }));
      return;
    }
    _setEntries((prev) => [...prev, newEntry()]);
  };

  const rowsOfZone = (zone) =>
    [...new Set(map.seats.filter((s) => s.zone === zone).map((s) => s.row))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );

  // เลขที่นั่งเฉพาะที่ว่าง (รวมที่นั่งของรายการนี้เอง) ไม่ซ้ำกับรายการอื่น
  const seatOptions = (entry, zone) =>
    map.seats
      .filter(
        (s) =>
          s.zone === zone &&
          s.row === entry.row &&
          (s.seat_id === entry.seat_id || (stateOf(s) === "free" && !selectedIds.includes(s.seat_id)))
      )
      .sort((a, b) => a.seat_no - b.seat_no);

  const _handleNext = async () => {
    const chosen = _entries.filter((e) => e.seat_id);
    if (!chosen.length) {
      toast.error(t("Please select at least one seat", { ns: "seat" }));
      return;
    }
    const names = Object.fromEntries(chosen.map((e) => [e.seat_id, e.viewer_name.trim()]));
    const conflicts = await onNext(
      chosen.map((e) => e.seat_id),
      names
    );
    if (Array.isArray(conflicts) && conflicts.length) {
      _setEntries((prev) => prev.map((e) => (conflicts.includes(e.seat_id) ? { ...e, seat_id: null } : e)));
    }
  };

  const areas = (info.zone_areas || []).filter((a) => zones.some((z) => z.zone === a.zone));

  return (
    <div className="seat_select">
      {info.seat_map_image && (
        <div className="ss_section">
          <div className="ss_heading">1.{t("Check the seating plan", { ns: "seat" })}</div>
          <div className="map_box">
            <img src={IMG_PATH + info.seat_map_image} alt="seat map" draggable={false} />
            {areas.map((area, i) => (
              <button
                type="button"
                key={`${area.zone}-${i}`}
                className={`map_area${area.zone === _zone ? " active" : ""}`}
                style={{ left: `${area.x}%`, top: `${area.y}%`, width: `${area.w}%`, height: `${area.h}%` }}
                aria-label={`${t("Zone", { ns: "seat" })} ${zoneLabel(area.zone)}`}
                onClick={() => _setZone(area.zone)}
              />
            ))}
            <button type="button" className="map_zoom" onClick={() => _setIsZoomOpen(true)} aria-label="zoom">
              <img src={`${ICON_PATH}/zoom-fit.svg`} alt="" width="24" height="24" />
            </button>
          </div>
        </div>
      )}

      <div className="ss_section">
        <div>
          <div className="ss_heading">{t("Select seats", { ns: "seat" })}</div>
          <div className="ss_note">
            * {t("Up to {{count}} seats per booking", { ns: "seat", count: info.max_per_booking })} ·{" "}
            {t("{{price}} THB per seat", { ns: "seat", price: RenderCommaMoney(info.price) })}
          </div>
        </div>
      </div>

      <div className="ss_section ss_gap10">
        <div className="zone_chips">
          {zones.map((z) => (
            <button
              type="button"
              key={z.zone}
              className={`zone_chip${z.zone === _zone ? " active" : ""}`}
              onClick={() => _setZone(z.zone)}
            >
              {zoneLabel(z.zone)}
            </button>
          ))}
        </div>
        <div className="ss_hint">
          ***{t("Tap a zone to view the plan, tap a light purple seat to select, or use the dropdowns below", { ns: "seat" })}
        </div>
      </div>

      <div className="ss_section ss_gap10">
        <div className="legend_row">
          {LEGEND.map((state) => (
            <div className="legend_item" key={state}>
              <span className="box" style={{ background: SEAT_STATE_STYLE[state].bg }} />
              {t(SEAT_STATE_STYLE[state].labelKey, { ns: "seat" })}
            </div>
          ))}
        </div>
        <Displays.SeatZoneGrid
          badge={_zone}
          badgeColor={ZONE_BADGE_COLOR(zoneIndex)}
          title={[
            `${t("Zone", { ns: "seat" })} ${_zone}`,
            zoneNameOf[_zone],
            `${t("Available", { ns: "seat" })} ${zoneInfo.free}/${zoneInfo.total}`,
          ]
            .filter(Boolean)
            .join(" · ")}
          seats={seatsOfZone}
          onToggle={_toggleSeat}
        />
      </div>

      <div className="ss_section ss_gap15">
        {_entries.map((entry, index) => {
          const zone = entry.zone || _zone;
          return (
            <div className="seat_entry" key={entry.uid}>
              <div className="entry_head">
                <div className="ss_heading">
                  Seat {index + 1} : {entry.seat_id || "-"}
                </div>
                {(_entries.length > 1 || entry.seat_id || entry.row) && (
                  <div className="entry_remove">
                    <Buttons.BgStandard
                      theme_white_purple
                      height="28px"
                      prefixLabel={<Icons.Trash width={13} height={13} />}
                      label={t("Remove", { ns: "seat" })}
                      onClick={() => _removeEntry(index)}
                    />
                  </div>
                )}
              </div>
              <div className="entry_selects">
                <div className="entry_field">
                  <div className="entry_label">{t("Row", { ns: "seat" })}</div>
                  <Inputs.DropdownSelect
                    theme_standard
                    menuPlacement="auto"
                    placeholder="-"
                    options={rowsOfZone(zone).map((row) => ({
                      label: entry.zone && entry.zone !== _zone ? `${entry.zone} · ${row}` : row,
                      value: row,
                    }))}
                    value={
                      entry.row
                        ? {
                            label: entry.zone && entry.zone !== _zone ? `${entry.zone} · ${entry.row}` : entry.row,
                            value: entry.row,
                          }
                        : null
                    }
                    onChange={(option) => _updateEntry(index, { zone, row: option.value, seat_id: null })}
                  />
                </div>
                <div className="entry_field">
                  <div className="entry_label">{t("Seat no. (available only)", { ns: "seat" })}</div>
                  <Inputs.DropdownSelect
                    theme_standard
                    menuPlacement="auto"
                    placeholder="-"
                    disabled={!entry.row}
                    options={seatOptions(entry, zone).map((s) => ({
                      label: String(s.seat_no).padStart(2, "0"),
                      value: s.seat_id,
                    }))}
                    value={
                      entry.seat_id
                        ? {
                            label: String((seatById.get(entry.seat_id) || {}).seat_no || "").padStart(2, "0"),
                            value: entry.seat_id,
                          }
                        : null
                    }
                    onChange={(option) => _updateEntry(index, { zone, seat_id: option.value })}
                  />
                </div>
              </div>
              <div className="entry_viewer">
                <div className="viewer_label">{t("Audience name per seat (optional)", { ns: "seat" })}</div>
                <input
                  className="viewer_input"
                  value={entry.viewer_name}
                  maxLength={100}
                  onChange={(e) => _updateEntry(index, { viewer_name: e.target.value })}
                />
              </div>
            </div>
          );
        })}
        <div className="add_seat_row">
          <button type="button" className="add_seat_btn" onClick={_addEntry} disabled={_entries.length >= info.max_per_booking}>
            <img src={`${ICON_PATH}/plus.svg`} alt="" width="18" height="18" />
            {t("Add seat", { ns: "seat" })}
          </button>
          <div className="total_text">
            {t("Total {{count}} seats = {{total}} THB", {
              ns: "seat",
              count: selectedIds.length,
              total: RenderCommaMoney(info.price * selectedIds.length),
            })}
          </div>
        </div>
      </div>

      <div className="btn_row">
        <div>
          <Buttons.BgStandard theme_white_purple label={t("Back", { ns: "seat" })} onClick={onBack} />
        </div>
        <div>
          <Buttons.BgStandard label={t("Next", { ns: "seat" })} onClick={_handleNext} />
        </div>
      </div>

      <Modals.BodyEmpty isShowModal={_isZoomOpen} onCloseModal={() => _setIsZoomOpen(false)}>
        {_isZoomOpen && (
          <div className="map_zoom_modal">
            <img src={IMG_PATH + info.seat_map_image} alt="seat map" />
            <Buttons.BgStandard theme_white_purple label={t("Close", { ns: "seat" })} onClick={() => _setIsZoomOpen(false)} />
          </div>
        )}
      </Modals.BodyEmpty>
    </div>
  );
};

export default SeatSelectStep;
